import { getCurrentClan } from "helpers/currentAccount";
import { fetchWrapper as fetch } from "helpers/fetch";
import { t } from "helpers/localization";
import { snakeToCamel } from "helpers/strings";
import get from "lodash/get";
import isBoolean from "lodash/isBoolean";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import { push } from "react-router-redux";
import { batchActions } from "redux-batched-actions";
import { hasPermission, permissions } from "roles";
import * as web2ClientAPI from "../web2ClientAPI/web2ClientAPI";
import { fetchApplications } from "./ActionApp";
import { changeClanAttributesSuccessed } from "./ActionClanEdit";
import { clearMembersOperation, fetchMembers, updateClanFromSync } from "./ActionClanProfile";
import { errorRenamingClan, stopRenamingClan } from "./ActionClanRename";
import { closeAllDialogs } from "./ActionDialog";
import { fetchInvites } from "./ActionInvites";
import { SyncSupply } from "./ActionSupply";
import { getCookie } from "../utils";
import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { currentAccount } from "~/Reducers/ReducerCurrentAccount";
import { reject } from "lodash";


export const UPDATE_ACCOUNT_INFO = 'UPDATE_ACCOUNT_INFO';
export const updateAccountInfo = (info: any) => {
  return {
    type: UPDATE_ACCOUNT_INFO,
    info
  }
};

export const DECREASE_ACCOUNT_TOTAL_GOLD = 'DECREASE_ACCOUNT_TOTAL_GOLD';
export const decreaseAccountTotalGold = (amount: number) => {
  return {
    type: DECREASE_ACCOUNT_TOTAL_GOLD,
    amount
  }
};

export const DECREASE_ACTIVE_ENTRIES_COUNT = 'DECREASE_ACTIVE_ENTRIES_COUNT';
export const decreaseActiveEntriesCount = () => {
  return{
    type: DECREASE_ACTIVE_ENTRIES_COUNT
  }
};

const sendNotificationsByAccountInfoSync = (state, newAccountInfo, overriddenMessages) => {
  const messages = Object.assign({
    joinClan: () => web2ClientAPI.sendCurrentAccountJoinedClanNotification(newAccountInfo.clan),
    leaveClan: () => web2ClientAPI.sendCurrentAccountRemovedFromClanNotification(getCurrentClan(state)),
    changeRole: () => web2ClientAPI.sendCurrentAccountRoleChangedNotification(newAccountInfo.role_name, getCurrentClan(state))
  }, overriddenMessages);
  const currentAccount = state.currentAccount;
  if (currentAccount.clanId !== newAccountInfo.clan_id) {
    if (!currentAccount.clanId) {
      messages.joinClan();
    } else if (!newAccountInfo.clan_id) {
      messages.leaveClan();
    } else {
      messages.joinClan();
      messages.leaveClan();
    }
  } else if (newAccountInfo.role_name && currentAccount.roleName !== newAccountInfo.role_name) {
    messages.changeRole();
  }

};

const getObjectsDiffFields = (fields, currentObject, newObject) => {
  const diffObject = {};
  fields.forEach(field => {
    const rightValue = newObject[field];
    if (isUndefined(rightValue)) return;

    const snakeCamelField = snakeToCamel(field);
    if (currentObject[snakeCamelField] !== rightValue) {
      diffObject[snakeCamelField] = rightValue;
    }
  });
  return diffObject;
};

const accountDiffFieldsToUpdate = (currentAccount, accountInfo) => {
  const fields = ['clan_id', 'active_invites_count', 'active_applications_count', 'accumulative_clan_resource', 'leveling', 'is_bonus_activated', 'in_clan_cooldown_till'];

  const accountInfoToUpdate = getObjectsDiffFields(fields, currentAccount, accountInfo);

  if (accountInfoToUpdate.clanId === null) {
    accountInfoToUpdate.activeApplicationsCount = 0;
    accountInfoToUpdate.accumulativeClanResource = null;
    accountInfoToUpdate.leveling = null;
    accountInfoToUpdate.isBonusActivated = null;
  } else if (accountInfoToUpdate.clanId && !currentAccount.clanId) {
    accountInfoToUpdate.activeInvitesCount = 0;
  }

  if (currentAccount.roleName !== accountInfo.role_name) {
    accountInfoToUpdate.role = accountInfo.role_name;
  }

  return accountInfoToUpdate;
};

const clanDiffFieldsToUpdate = (currentAccountClanInfo, newClanInfo) => {
  const fields = ['max_members_count', 'accumulative_resource', 'leveling', 'personal_resource'];
  return getObjectsDiffFields(fields, currentAccountClanInfo, newClanInfo);
};

const getFetchUrl = (state) => {
  let queryParams = {};
  const {
    isProcessing,
    isCheckingStatus
  } = state.clanRename;

  if (isProcessing && !isCheckingStatus) {
    queryParams.is_fetching_rename_info = 1;
  }

  return { url: state.urls.accountInfoSync, queryParams };
};

const processUpdateAccountRole = (state, dispatch, accountInfoToUpdate) => {
  const currentAccount = state.currentAccount;
  if (currentAccount.clanId && currentAccount.roleName && state.routing.locationBeforeTransitions.pathname === `${state.urls.root}profile`) {
    dispatch(clearMembersOperation());
    dispatch(fetchMembers(currentAccount.clanId));
  }

  if (!hasPermission(accountInfoToUpdate.role, permissions.HANDLE_INVITES) && state.routing.locationBeforeTransitions.pathname === `${state.urls.root}invites`) {
    dispatch(push(state.urls.root));
  }
  if (!hasPermission(accountInfoToUpdate.role, permissions.RENAME_CLAN) && state.routing.locationBeforeTransitions.pathname === `${state.urls.root}profile`) {
    dispatch(stopRenamingClan());
  }
};

const processRenamingInfo = (state, dispatch, renamingInfo, clanId) => {
  const isOwn = get(renamingInfo, 'is_own');
  const status = get(renamingInfo, 'status');
  if (isOwn) {
    if (status === 'ok') {
      const currentClan = getCurrentClan(state);
      const tagAndName = {
        name: get(renamingInfo, 'name'),
        tag: get(renamingInfo, 'tag')
      };
      web2ClientAPI.sendClanRenameCompleteNotification(currentClan, tagAndName, renamingInfo.cost);
      dispatch(batchActions([errorRenamingClan(null), changeClanAttributesSuccessed({
        ...tagAndName,
        clanId
      })]));
    } else if (status === 'error') {
      const reason = get(renamingInfo, 'error') === 'NO_GOLD' ? 'no_gold' : null;
      const error = reason === 'no_gold' ? t('Недостаточно средств') : t('Произошла ошибка. Повторите попытку позже');
      web2ClientAPI.sendClanRenameErrorNotification(reason);
      dispatch(errorRenamingClan({ error }));
    }
  } else {
    const actions = [errorRenamingClan(null)];
    if (status === 'ok') {
      actions.push(changeClanAttributesSuccessed({
        name: get(renamingInfo, 'name'),
        tag: get(renamingInfo, 'tag'),
        clanId
      }));
    }
    dispatch(batchActions(actions));
  }
};

export const SYNC_CURRENT_ACCOUNT = 'SYNC_CURRENT_ACCOUNT';
export const syncCurrentAccount = (json: any): ThunkAction<void, {}, {}, AnyAction> => (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: () => State) => {
  const newBuildings = [];
  const buildings = get(json, ['clan', 'buildings'], {});

  for (const key in buildings) {
    if (buildings.hasOwnProperty(key)) {
      const building = buildings[key];
      newBuildings.push({
        id: building.id,
        level: building.level,
        modifiers: building.modifiers,
        type: building.name
      });
    }
  }

  dispatch({
    type: SYNC_CURRENT_ACCOUNT,
    payload: {
      buildings: newBuildings
    }
  });
};


export const syncAccountInfo = (overriddenNotifications = {}): ThunkAction<void, {}, {}, AnyAction> => (dispatch: ThunkDispatch<{}, {}, AnyAction>, getState: () => State) => {
  const state = getState();
  const { url, queryParams } = getFetchUrl(state);
  if (getCookie(state.settings.wsauthTokenName)){
    return fetch(url, { query: queryParams }).then(json => {
      if (json && !json.error && !json.additional_info) {
        const state = getState();
        const currentAccount = state.currentAccount;

        dispatch(syncCurrentAccount(json));
        sendNotificationsByAccountInfoSync(state, json, overriddenNotifications);

        const actions = [];
        if (!isEmpty(json.clan) && currentAccount.clanId && currentAccount.clanId === json.clan.id) {
          const currentAccountClan = getCurrentClan(state);
          const clanInfoToUpdate = clanDiffFieldsToUpdate(currentAccountClan, json.clan);
          if (!isEmpty(clanInfoToUpdate)) {
            actions.push(updateClanFromSync(currentAccount.clanId, clanInfoToUpdate));
          }
        }

        actions.push(SyncSupply(json));
        
        const accountInfoToUpdate = accountDiffFieldsToUpdate(currentAccount, json);

        if (!isEmpty(accountInfoToUpdate)) {
          actions.push(updateAccountInfo(accountInfoToUpdate));
        }

        if (!isEmpty(actions)) {
          actions.length > 1 ? dispatch(batchActions(actions)) : dispatch(actions[0]);
        }

        if (accountInfoToUpdate.role) {
          processUpdateAccountRole(state, dispatch, accountInfoToUpdate);
        }

        if (!isUndefined(accountInfoToUpdate.clanId)) {
          dispatch(closeAllDialogs());
          dispatch(push(state.urls.root));
        }

        if (!isUndefined(accountInfoToUpdate.activeInvitesCount)) {
          if (isUndefined(accountInfoToUpdate.clanId) && currentAccount.activeInvitesCount !== accountInfoToUpdate.activeInvitesCount && state.routing.locationBeforeTransitions.pathname === `${state.urls.root}requests`) {
            dispatch(fetchInvites());
          }
        }

        if (!isUndefined(accountInfoToUpdate.activeApplicationsCount)) {
          if (currentAccount.activeApplicationsCount !== accountInfoToUpdate.activeApplicationsCount && state.routing.locationBeforeTransitions.pathname === `${state.urls.root}requests`) {
            dispatch(fetchApplications());
          }
        }

        if (json.is_renaming === false && state.routing.locationBeforeTransitions.pathname === `${state.urls.root}profile`) {
          processRenamingInfo(state, dispatch, json.renaming_info || {}, json.clan_id);
        }
      }
    }).catch(err => {
      console.error('Failed sync account info', err);
      throw err;
    });
  } else {
    return new Promise((resolve, reject) => {
      resolve();
    });
  }
};